import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { IBankaDto, SharedClient } from '@kodit/core/data-api';
import {
  FormBuilderTypeSafe,
  FormGroupTypeSafe,
} from 'angular-typesafe-reactive-forms-helper';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

interface IRequest {
  searchValue: IBankaDto | string;
}

@Component({
  selector: 'kodit-banka-autocomplete',
  templateUrl: './banka-autocomplete.component.html',
  styleUrls: ['./banka-autocomplete.component.scss'],
})
export class BankaAutocompleteComponent
  implements OnInit, OnDestroy, OnChanges {
  /** Subs */
  private _subs: Subscription = new Subscription();
  private _search$ = new Subject<string>();

  /** Props */
  formGroup: FormGroupTypeSafe<IRequest>;
  bankaSuggestions: IBankaDto[] = [];

  private _selectedBanka: IBankaDto | null = null;

  /** I/O */
  @Input() mustSelect: boolean = false;
  @Input() formCtrlId: string;
  @Input() inputInitValue: string;
  @Output() onBankaChange = new EventEmitter<IBankaDto>();

  constructor(
    private _client: SharedClient,
    private _fb: FormBuilderTypeSafe
  ) {}

  ngOnInit(): void {
    this.formGroup = this._fb.group<IRequest>({
      searchValue: new FormControl(this._toBankaValue(this.inputInitValue)),
    });

    this._subs.add(
      this._search$
        .pipe(
          debounceTime(300),
          switchMap((query) => this._client.getBanke(query))
        )
        .subscribe({
          next: (result) => {
            this.bankaSuggestions = [...(result.data?.bankeDtoList ?? [])];
          },
          error: () => {
            this.bankaSuggestions = [];
          },
        })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const initValueChange = changes['inputInitValue'];
    if (
      initValueChange &&
      !initValueChange.isFirstChange() &&
      initValueChange.currentValue &&
      initValueChange.currentValue !== initValueChange.previousValue
    ) {
      const banka: IBankaDto = { naziv: initValueChange.currentValue };
      this._selectedBanka = banka;
      this.formGroup.controls.searchValue.patchValue(banka, {
        emitEvent: false,
      });
    }
  }

  handleSearch(event: { query: string }) {
    const query = (event?.query ?? '').trim();
    if (query.length < 1) {
      this.bankaSuggestions = [];
      return;
    }
    this._search$.next(query);
  }

  selectionChanged(banka: IBankaDto) {
    this._selectedBanka = banka;
    this.onBankaChange.emit(banka);
  }

  onBlur() {
    if (
      this._selectedBanka &&
      this.formGroup.value.searchValue !== this._selectedBanka
    ) {
      this.formGroup.controls.searchValue.patchValue(this._selectedBanka, {
        emitEvent: false,
      });
    }
  }

  onClear() {
    this.bankaSuggestions = [];
  }

  private _toBankaValue(naziv: string): IBankaDto | null {
    return naziv ? ({ naziv } as IBankaDto) : null;
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
    this._search$.complete();
  }
}
